'use client';

import { datadogRum } from '@datadog/browser-rum';
import { useEffect, useState } from 'react';

import { authClient } from '@/lib/auth';
import { logger } from '@/lib/logger';

/**
 * Datadog User Identification Component
 *
 * Automatically identifies users with Datadog RUM when authenticated.
 * - Watches better-auth session for changes
 * - Sets user context on Datadog RUM after login
 * - Clears user context on logout
 *
 * This component renders nothing - it only handles side effects.
 */
export function DatadogIdentify() {
  const { data: session } = authClient.useSession();
  const [lastIdentifiedUserId, setLastIdentifiedUserId] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (session?.user && !lastIdentifiedUserId) {
      const { id, email, name } = session.user;

      try {
        datadogRum.setUser({
          id,
          email: email ?? undefined,
          name: name ?? undefined,
        });
        setLastIdentifiedUserId(id);

        logger.info('User identified in Datadog', { userId: id, email });
      } catch (error) {
        logger.captureException(error as Error, {
          context: { userId: id, email },
          tags: {
            feature: 'analytics',
            provider: 'datadog',
            action: 'identify',
          },
        });
      }
    } else if (!session?.user && lastIdentifiedUserId) {
      try {
        datadogRum.clearUser();
        setLastIdentifiedUserId(null);

        logger.info('User identity cleared in Datadog');
      } catch (error) {
        logger.captureException(error as Error, {
          tags: { feature: 'analytics', provider: 'datadog', action: 'clear' },
        });
      }
    }
  }, [session?.user, lastIdentifiedUserId]);

  return null;
}

export default DatadogIdentify;
